import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import { defaultKeepKey, type MergeCandidate } from "@/lib/customerMerge";

export type { MergeCandidate };

interface MergeCustomersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  businessId: string;
  candidates: [MergeCandidate, MergeCandidate] | null;
  customerTerm: string;
  onMerged: () => void;
}

const joinedLabel = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? null
    : `Joined ${new Intl.DateTimeFormat("en", { day: "numeric", month: "short", year: "numeric" }).format(date)}`;
};

const MergeCustomersDialog = ({
  open,
  onOpenChange,
  businessId,
  candidates,
  customerTerm,
  onMerged,
}: MergeCustomersDialogProps) => {
  const [keepKey, setKeepKey] = useState<string | null>(null);
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    if (open && candidates) setKeepKey(defaultKeepKey(candidates));
    if (!open) setMerging(false);
  }, [open, candidates]);

  if (!candidates) return null;
  const keep = candidates.find((candidate) => candidate.key === keepKey) ?? candidates[0];
  const other = candidates.find((candidate) => candidate.key !== keep.key) ?? candidates[1];
  const term = customerTerm.toLowerCase();

  const merge = async () => {
    setMerging(true);
    const { error } = await (supabase as any).rpc("merge_customer_profiles", {
      p_business_id: businessId,
      p_keep_customer_id: keep.customerId,
      p_keep_contact_id: keep.contactId,
      p_merge_customer_id: other.customerId,
      p_merge_contact_id: other.contactId,
    });
    setMerging(false);
    if (error) {
      toast.error(`Couldn't merge these ${term}s: ${error.message}`);
      return;
    }
    toast.success(`Merged into ${keep.name}`);
    onOpenChange(false);
    onMerged();
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !merging && onOpenChange(next)}>
      <DialogContent className="admin-modal-content sm:max-w-[560px]">
        <DialogHeader className="admin-modal-header">
          <DialogTitle className="font-heading">Merge 2 {term}s</DialogTitle>
          <DialogDescription>
            Choose the profile to keep. The other one's chats, bookings, notes, groups and channels move onto it, and its details fill any blanks.
          </DialogDescription>
        </DialogHeader>

        <RadioGroup
          value={keep.key}
          onValueChange={setKeepKey}
          aria-label={`${customerTerm} to keep`}
          className="grid gap-2 sm:grid-cols-2"
        >
          {candidates.map((candidate) => {
            const selected = candidate.key === keep.key;
            const details = [candidate.identity, candidate.phone, candidate.email]
              .filter((value, index, values): value is string => !!value && values.indexOf(value) === index);
            return (
              <label
                key={candidate.key}
                className={cn(
                  "flex min-w-0 cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors",
                  selected ? "border-foreground/35 bg-muted/60" : "border-border hover:bg-muted/40",
                )}
              >
                <RadioGroupItem
                  value={candidate.key}
                  aria-label={`Keep ${candidate.name}`}
                  className="mt-0.5 border-foreground text-foreground"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-foreground">{candidate.name}</span>
                  {details.map((detail) => (
                    <span key={detail} className="block truncate text-xs leading-5 text-muted-foreground">{detail}</span>
                  ))}
                  <span className="mt-1 block text-xs text-muted-foreground">
                    {[
                      `${candidate.totalBookings} booking${candidate.totalBookings === 1 ? "" : "s"}`,
                      joinedLabel(candidate.createdAt),
                    ].filter(Boolean).join(" · ")}
                  </span>
                  <span className={cn("mt-2 block text-xs font-medium", selected ? "text-foreground" : "text-muted-foreground")}>
                    {selected ? "Keep" : "Merge into the other"}
                  </span>
                </span>
              </label>
            );
          })}
        </RadioGroup>

        <p className="text-xs leading-5 text-muted-foreground">
          {other.name} won't appear as a separate {term} after this. Merging can't be undone from the dashboard.
        </p>

        <DialogFooter>
          <Button type="button" variant="outline" disabled={merging} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" disabled={merging} onClick={merge}>
            {merging && <Loader2 className="h-4 w-4 animate-spin" />}
            {merging ? "Merging…" : "Merge"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MergeCustomersDialog;
